import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { App } from '@capacitor/app';
import { register } from 'swiper/element/bundle';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import { AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './services/auth.service';
import { PermissionsService } from './services/permissions.service';
import { UsageTrackingService } from './services/usage-tracking.service';
import { SatisfactionSurveyService } from './services/satisfaction-survey.service';
register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private static readonly PAM_STORAGE_KEY = 'pam_mode';

  showPamFab = false;
  private fullscreenAlertOpen = false;
  private fullscreenListenerSet = false;

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private alertController: AlertController,
    private storage: Storage,
    private router: Router,
    private zone: NgZone,
    private usageTracking: UsageTrackingService,
    private satisfactionSurvey: SatisfactionSurveyService,
  ) {
    addIcons({ ...allIcons });
    // El idioma se configura y se espera (await) en el provideAppInitializer de
    // main.ts, antes de arrancar la app. No lo dupliques aquí: hacerlo provoca
    // una segunda carga que compite con la primera y deja ver las claves crudas.
  }

  async ngOnInit(): Promise<void> {
    await this.initDeepLinks();
    await this.handlePamMode();
    await this.authService.waitUntilReady();
    if (this.authService.isAuthenticated()) {
      await this.permissionsService.loadUserPermissions();

      // Medición de uso: registra la apertura de app y vuelca lo que quedara
      // pendiente de la sesión anterior.
      await this.usageTracking.start();

      // Encuesta de satisfacción: se ofrece en cada arranque mientras el usuario
      // no la haya respondido y tenga las interacciones necesarias. No se espera
      // (`void`) para no retrasar el arranque.
      void this.satisfactionSurvey.maybePrompt();
    }
  }

  /**
   * Deep links (App Links en Android / Universal Links en iOS).
   *
   * Cuando el usuario abre un enlace https de valorea.i4life.es y tiene la app
   * instalada, el SO abre la app en vez del navegador y nos entrega la URL
   * completa. Como el routing es por hash (useHash: true), la ruta real va tras
   * el "#" (p.ej. "https://valorea.i4life.es/#/signup?token=...&email=...").
   * Extraemos ese fragmento y navegamos con el Router conservando query params.
   *
   * - appUrlOpen: se dispara con la app ya en ejecucion (warm start).
   * - getLaunchUrl: cubre el arranque en frio (la app estaba cerrada).
   *
   * En web (donde el plugin @capacitor/app es no-op) esto no hace nada: el
   * enrutado normal del navegador ya resuelve el hash.
   */
  private async initDeepLinks(): Promise<void> {
    App.addListener('appUrlOpen', ({ url }) => {
      this.navigateFromDeepLink(url);
    });

    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      this.navigateFromDeepLink(launch.url);
    }
  }

  private navigateFromDeepLink(url: string): void {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) {
      return;
    }

    // Todo lo que va tras el "#": "/signup?token=...&email=..." (con o sin la
    // barra inicial segun el enlace). Router.navigateByUrl parsea la query.
    let route = url.substring(hashIndex + 1);
    if (!route.startsWith('/')) {
      route = '/' + route;
    }

    // Fuera de la zona de Angular (viene del bridge nativo): re-entrar para que
    // la deteccion de cambios se ejecute tras navegar.
    this.zone.run(() => this.router.navigateByUrl(route));
  }

  /**
   * El modo PAM se activa al entrar con el queryParam ?pam=true y se guarda
   * de forma persistente en el storage para que sobreviva a las recargas.
   * (Al cerrar sesión, AuthService.logout() limpia el storage y lo elimina.)
   * Cuando está activo se muestra el FAB que redirige a pam2.i4life.es y se
   * avisa al usuario para activar el modo pantalla completa (requiere gesto
   * del usuario).
   */
  private async handlePamMode(): Promise<void> {
    await this.storage.create();

    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('pam') === 'true';
    const stored = await this.storage.get(AppComponent.PAM_STORAGE_KEY);

    if (fromUrl && !stored) {
      await this.storage.set(AppComponent.PAM_STORAGE_KEY, true);
    }

    if (!fromUrl && !stored) {
      return;
    }

    this.showPamFab = true;
    // this.listenForFullscreenExit();
    // this.presentFullscreenAlert('pam.fullscreen_title', 'pam.fullscreen_message');
  }

  /**
   * Detecta cuándo el usuario sale del modo pantalla completa (Esc, gesto del
   * sistema, etc.) y, estando en modo PAM, vuelve a pedir que lo reactive.
   */
  private listenForFullscreenExit(): void {
    if (this.fullscreenListenerSet) {
      return;
    }
    this.fullscreenListenerSet = true;

    const onChange = () => {
      const isFullscreen = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
      if (!isFullscreen) {
        this.presentFullscreenAlert('pam.fullscreen_stopped_title', 'pam.fullscreen_stopped_message');
      }
    };

    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
  }

  private async presentFullscreenAlert(titleKey: string, messageKey: string): Promise<void> {
    if (this.fullscreenAlertOpen) {
      return;
    }
    this.fullscreenAlertOpen = true;

    const [header, message, continueText] = await Promise.all([
      firstValueFrom(this.translate.get(titleKey)),
      firstValueFrom(this.translate.get(messageKey)),
      firstValueFrom(this.translate.get('pam.fullscreen_continue')),
    ]);

    const alert = await this.alertController.create({
      header,
      message,
      backdropDismiss: false,
      buttons: [
        {
          text: continueText,
          handler: () => {
            this.enterFullscreen();
          },
        },
      ],
    });

    alert.onDidDismiss().then(() => {
      this.fullscreenAlertOpen = false;
    });

    await alert.present();
  }

  private enterFullscreen(): void {
    const el = document.documentElement;
    const request = el.requestFullscreen || (el as any).webkitRequestFullscreen || (el as any).msRequestFullscreen;

    request?.call(el)?.catch(() => undefined);
  }

  goToPam(): void {
    window.location.href = 'https://pam2.i4life.es/#/main';
  }
}
